import { useCallback } from 'react'
import { Platform, ScrollView } from 'react-native'
import { useTranslation } from 'react-i18next'
import { reloadAppAsync } from 'expo'
import { FallbackProps as Props } from 'react-error-boundary'

import AppConfig from 'config'
import { useLogout } from 'hooks/useLogout'
import { useErrorScreenUpdate } from 'hooks/useErrorScreenUpdate'
import Typography from '@ui/typography'
import ScreenTemplate from '@shell/ScreenTemplate'

export function GlobalErrorScreen({ error }: Props) {
  const {
    isCheckingUpdate,
    isDownloadingUpdate,
    updateMessage,
    checkForUpdate,
  } = useErrorScreenUpdate()

  const reset = useCallback(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      window.location.replace('/')
      return
    }

    reloadAppAsync('error boundary reload')
  }, [])

  const buttons = [
    {
      label: 'Check for Update',
      onPress: checkForUpdate,
      loading: isCheckingUpdate || isDownloadingUpdate,
    },
    {
      label: 'Reload',
      onPress: reset,
    },
  ]

  return (
    <ScreenTemplate
      icon="exclamation-circle"
      title="Error"
      desc="An error has occurred"
      buttons={buttons}
    >
      {updateMessage && (
        <Typography variant="MD" className="mb-4 text-center color-white">
          {updateMessage}
        </Typography>
      )}
      {AppConfig.isDev && error && (
        <ScrollView
          style={{ height: 160, width: 682 }}
          className="rounded-lg border border-white/10 bg-white/5 p-16"
          contentContainerClassName="items-center justify-center "
        >
          <Typography variant="MD" className="text-center color-white">
            {JSON.stringify(error.toString(), null, 2)}
          </Typography>
        </ScrollView>
      )}
    </ScreenTemplate>
  )
}

export default function ErrorScreen({ error }: Props) {
  const { t } = useTranslation(['ui', 'app'])
  const text = 'app:error'
  const {
    isCheckingUpdate,
    isDownloadingUpdate,
    updateMessage,
    checkForUpdate,
  } = useErrorScreenUpdate()

  const logout = useLogout({
    navigate: false,
  })

  const reset = useCallback(() => {
    if (!AppConfig.isDev) {
      logout()
    }

    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      window.location.replace('/')
      return
    }

    reloadAppAsync('error boundary reload')
  }, [logout])

  const buttons = [
    {
      label: t('ui:checkForUpdate', 'Check for Update'),
      onPress: checkForUpdate,
      loading: isCheckingUpdate || isDownloadingUpdate,
    },
    {
      label: t('ui:reload'),
      onPress: reset,
    },
  ]

  return (
    <ScreenTemplate
      icon="exclamation-circle"
      title={t(`${text}.header.title`)}
      desc={t(`${text}.header.desc`)}
      buttons={buttons}
    >
      {updateMessage && (
        <Typography variant="MD" className="mb-4 text-center color-white">
          {updateMessage}
        </Typography>
      )}
      {AppConfig.isDev && error && (
        <ScrollView
          style={{ height: 160, width: 682 }}
          className="rounded-lg border border-white/10 bg-white/5 p-16"
          contentContainerClassName="items-center justify-center "
        >
          <Typography variant="MD" className="text-center color-white">
            {JSON.stringify(error.toString(), null, 2)}
          </Typography>
        </ScrollView>
      )}
    </ScreenTemplate>
  )
}
